import React, { useState, useEffect } from 'react';
import { q3gRate } from './q3gRates';

// Locked Labor Plan (LLP) updates: copy-ready Full Day (prior day), Start of
// Shift and Mid Shift lines. Read-only: every number comes from caches Jar Jar
// already syncs (ALPS_<site>, IPT_<site>, FCLM day/night, ONSITE_, ROSTER_).
//
// Sources:
//   EOD Backlog   actual IPT 'End Vendor Freight Backlog' vs IPT 'Forecasted Locked End Vendor Freight Backlog'
//   Assignments   actual IPT 'New Vendor Freight' vs ALPS INBOUND ASSIGNMENT (day+night)
//   Cartons       actual FCLM inbound cartons (day+night) vs ALPS IB show hrs x IB CPLH (per shift)
//   Show hours    ALPS SHOW_HOURS ACTUALS vs FORECAST — IB, and IB+DA
//   CPLH          actual diluted (IB+OB cartons / FC Summary hrs) vs Q3G THROUGHPUT (PPID 609) — Q3G, not LLP
//   Density       IPT 'Vendor Receipts' units / actual IB cartons vs ALPS INBOUND WEIGHTED_AVERAGE_DENSITY
//   Staffing      T1 (FC Associate I) + FC Associate III in depts 1299010/1299040 (ROSTER_.hcExtra) clocked in vs ALPS IB+DA show-hours plan / 10 paid hrs

const PAID_HOURS_PER_HC = 10;

// Day-shift window (site-local minutes) used to pick the current shift.
const DAY_WINDOW = { OLM1: [8 * 60, 18 * 60 + 30], SBD3: [7 * 60, 17 * 60 + 30], SCK8: [7 * 60, 17 * 60 + 30] };

function addDays(iso, n) {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d + n));
  return dt.toISOString().slice(0, 10);
}
function sundayOf(iso) {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  return addDays(iso, -dt.getUTCDay());
}
function pacificNow() {
  const p = new Intl.DateTimeFormat('en-US', { timeZone: 'America/Los_Angeles', hour12: false, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })
    .formatToParts(new Date()).reduce((o, x) => { o[x.type] = x.value; return o; }, {});
  return { date: `${p.year}-${p.month}-${p.day}`, min: (Number(p.hour) % 24) * 60 + Number(p.minute), hhmm: `${p.hour}:${p.minute}` };
}
// Current shift + its shift date (night before day-start belongs to yesterday).
function currentShift(site) {
  const now = pacificNow();
  const [ds, de] = DAY_WINDOW[site] || DAY_WINDOW.OLM1;
  if (now.min >= ds && now.min < de) return { shift: 'day', date: now.date, now };
  if (now.min < ds) return { shift: 'night', date: addDays(now.date, -1), now };
  return { shift: 'night', date: now.date, now };
}

const getJSON = (url) => fetch(url).then(r => (r.ok ? r.json() : null)).catch(() => null);
const num = (v) => (typeof v === 'number' && isFinite(v) ? v : null);
const fmtN = (v, dp = 0) => (v == null ? '—' : v.toLocaleString(undefined, { minimumFractionDigits: dp, maximumFractionDigits: dp }));
const delta = (a, p) => (a != null && p ? (a / p - 1) * 100 : null);
const fmtPct = (x) => (x == null ? '—' : `${x >= 0 ? '+' : ''}${x.toFixed(1)}%`);

export default function LLPUpdates({ api, site, date }) {
  const [src, setSrc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState('');
  const cur = currentShift(site);
  const pd = addDays(date, -1);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const c = (d, st) => getJSON(`${api}/api/fclm/cache?site=${site}&date=${d}&shift_type=${st}`);
    Promise.all([
      c(`ALPS_${site}`, 'data'), c(`IPT_${site}`, 'data'),
      c(pd, 'day'), c(pd, 'night'),
      c(`ONSITE_${site}_${cur.date}`, cur.shift), c(`ROSTER_${site}`, 'data'),
    ]).then(([alps, ipt, pdDay, pdNight, onsite, roster]) => {
      if (alive) { setSrc({ alps, ipt, pdDay, pdNight, onsite, roster }); setLoading(false); }
    });
    return () => { alive = false; };
    // eslint-disable-next-line
  }, [api, site, pd, cur.date, cur.shift]);

  if (!src) return <div className="card"><p style={{ fontSize: '0.8rem' }}>{loading ? 'Loading LLP updates…' : 'No data.'}</p></div>;

  const alpsRows = (src.alps && src.alps.rows) || [];
  const iptRows = (src.ipt && src.ipt.rows) || [];
  const al = (d, concept, aspect, mp, shift) => {
    const rs = alpsRows.filter(r => r.date === d && r.concept === concept && r.aspect === aspect && (!mp || r.mainProcess === mp) && (!shift || r.shift === shift));
    return rs.length ? rs.reduce((s, r) => s + (r.value || 0), 0) : null;
  };
  const ip = (d, metric) => num((iptRows.find(r => r.date === d && r.metric === metric) || {}).value);
  const shiftOk = (x) => x && x.cartons && x.ppr_hours;
  const sumHrs = (x, k) => Object.values((x.ppr_hours && x.ppr_hours[k]) || {}).reduce((s, v) => s + (v || 0), 0);

  // ---- Full Day (PD) ----
  const bl = ip(pd, 'End Vendor Freight Backlog');
  const blP = ip(pd, 'Forecasted Locked End Vendor Freight Backlog');
  const asg = ip(pd, 'New Vendor Freight');
  const asgP = al(pd, 'ASSIGNMENT', 'FORECAST', 'INBOUND');
  const both = shiftOk(src.pdDay) && shiftOk(src.pdNight);
  const ibCtn = both ? (src.pdDay.cartons.inbound.total || 0) + (src.pdNight.cartons.inbound.total || 0) : null;
  const obCtn = both ? (src.pdDay.cartons.outbound.total || 0) + (src.pdNight.cartons.outbound.total || 0) : null;
  const fcHrs = both ? sumHrs(src.pdDay, 'fcSummary') + sumHrs(src.pdNight, 'fcSummary') : null;
  const shifts = ['DAY_SHIFT', 'NIGHT_SHIFT'];
  const ctnP = shifts.every(s => al(pd, 'SHOW_HOURS', 'FORECAST', 'INBOUND', s) != null && al(pd, 'CPLH', 'FORECAST', 'INBOUND', s) != null)
    ? shifts.reduce((t, s) => t + al(pd, 'SHOW_HOURS', 'FORECAST', 'INBOUND', s) * al(pd, 'CPLH', 'FORECAST', 'INBOUND', s), 0) : null;
  const ibH = al(pd, 'SHOW_HOURS', 'ACTUALS', 'INBOUND'), ibHP = al(pd, 'SHOW_HOURS', 'FORECAST', 'INBOUND');
  const allH = al(pd, 'SHOW_HOURS', 'ACTUALS'), allHP = al(pd, 'SHOW_HOURS', 'FORECAST');
  const dil = ibCtn != null && fcHrs ? (ibCtn + obCtn) / fcHrs : null;
  const q3g = q3gRate('609', sundayOf(pd)) || null;   // OLM1 Q3G only; null elsewhere
  const rcv = ip(pd, 'Vendor Receipts');
  const den = rcv != null && ibCtn ? rcv / ibCtn : null;
  const denP = al(pd, 'WEIGHTED_AVERAGE_DENSITY', 'FORECAST', 'INBOUND', 'DAY_SHIFT');

  const fullDay = [
    `PD EOD Backlog Units ${fmtN(bl)} actual EOD Backlog vs. ${fmtN(blP)} LLP EOD BL or ${fmtPct(delta(bl, blP))}`,
    `PD Assignment Units ${fmtN(asg)} actual Assignments vs. ${fmtN(asgP)} LLP Assignments or ${fmtPct(delta(asg, asgP))}`,
    `PD Carton Execution ${fmtN(ibCtn)} actual IB cartons vs. ${fmtN(ctnP)} LLP cartons or ${fmtPct(delta(ibCtn, ctnP))}`,
    `PD show hours ${fmtN(ibH)} actual IB show hours vs. ${fmtN(ibHP)} LLP hours or ${fmtPct(delta(ibH, ibHP))} | IB+DA ${fmtN(allH)} vs. ${fmtN(allHP)} or ${fmtPct(delta(allH, allHP))}`,
    `PD CPLH Cost Execution ${fmtN(dil, 1)} actual diluted CPLH vs. ${q3g ? fmtN(q3g, 1) : '—'} Q3G CPLH (Q3G, not LLP) or ${fmtPct(delta(dil, q3g))}`,
    `PD Density ${fmtN(den, 2)} actual vs. ${fmtN(denP, 2)} LLP density or ${fmtPct(delta(den, denP))}`,
  ];

  // ---- Staffing (SOS / Mid Shift) ----
  // Headcount population: T1s (FC Associate I) + FC Associate III in depts 1299010 / 1299040
  // (ROSTER_.hcExtra, written by rollcall-roster-sync.sh).
  const t1 = new Set([
    ...Object.keys((src.roster && src.roster.empToBb) || {}),
    ...Object.keys((src.roster && src.roster.hcExtra) || {}),
  ].map(String));
  const onsite = (src.onsite && Array.isArray(src.onsite.onsite)) ? src.onsite.onsite : null;
  const t1In = onsite && t1.size ? onsite.filter(x => x.currentlyIn && t1.has(String(x.empId))).length : null;
  const planKey = cur.shift === 'day' ? 'DAY_SHIFT' : 'NIGHT_SHIFT';
  const planHrs = al(cur.date, 'SHOW_HOURS', 'FORECAST', null, planKey);
  const hcP = planHrs != null ? planHrs / PAID_HOURS_PER_HC : null;
  const staffing = `Current Day staffing, ${fmtN(t1In)} actual on premise HC vs. ${fmtN(hcP)} hours plan ${fmtPct(delta(t1In, hcP))} risk`;
  const asOf = src.onsite && src.onsite.fetched_at ? new Date(src.onsite.fetched_at).toLocaleTimeString('en-US', { timeZone: 'America/Los_Angeles', hour: '2-digit', minute: '2-digit' }) : null;

  const copy = (label, text) => {
    navigator.clipboard.writeText(text).then(() => { setCopied(label); setTimeout(() => setCopied(''), 2000); });
  };
  const box = { fontFamily: 'Arial, sans-serif', fontSize: '0.85rem', background: '#f7f7f7', border: '1px solid #ddd', borderRadius: 4, padding: '8px 12px', whiteSpace: 'pre-wrap', margin: '6px 0 12px' };
  const btn = { background: '#455a64', color: '#fff', border: 'none', padding: '4px 12px', borderRadius: 4, cursor: 'pointer', fontSize: '0.75rem' };
  const head = (title, label, text) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <h4 style={{ margin: 0, fontSize: '0.9rem' }}>{title}</h4>
      <button style={btn} onClick={() => copy(label, text)}>📋 Copy</button>
      {copied === label && <span style={{ fontSize: '0.75rem', color: '#2e7d32' }}>Copied</span>}
    </div>
  );
  const fullText = `Full Day Update (PD ${pd})\n` + fullDay.join('\n');
  const sosText = `Start of shift Update (${cur.date} ${cur.shift})\n${staffing}`;
  const midText = `Mid shift Update (${cur.date} ${cur.shift}${asOf ? `, as of ${asOf} PT` : ''})\n${staffing}`;

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <h3 className="section-title" style={{ marginTop: 0 }}>Locked Labor Plan Updates — {site}</h3>
      {head(`Full Day Update — PD ${pd}`, 'full', fullText)}
      <div style={box}>{fullDay.join('\n')}</div>
      {head(`Start of Shift — ${cur.date} ${cur.shift}`, 'sos', sosText)}
      <div style={box}>{staffing}</div>
      {head(`Mid Shift — ${cur.date} ${cur.shift}${asOf ? ` (as of ${asOf} PT)` : ''}`, 'mid', midText)}
      <div style={box}>{staffing}</div>
      <p style={{ fontSize: '0.7rem', color: '#666', margin: 0 }}>
        LLP = ALPS / IPT locked plan. CPLH plan is Q3G (not LLP). Staffing = T1 (FC Associate I) currently clocked in vs.
        ALPS IB+DA show-hours plan ÷ {PAID_HOURS_PER_HC} paid hrs. PD = selected date − 1.
      </p>
    </div>
  );
}
